/**
 * Created by Amit on 2/7/2018.
 */

import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {IWinRatioConfig, WinRatioType} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {Timeline, TimelineLite, TweenLite} from "gsap";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {AnimatedParticle, Emitter} from "pixi-particles";
import {BonusBunniesParticleManager} from "../../BonusBunniesParticleManager";
import {ParticleConfigs} from "../../particles/ParticleConfigs";
import {BigWinCoinsFountain} from "../../coinfountain/BigWinCoinsFountain";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {BonusBunniesSoundConfig} from "../../BonusBunniesSoundConfig";
import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {BonusBunniesGameConfig} from "../../BonusBunniesGameConfig";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";

export class BonusBunniesBigWinView extends BaseView {

    private _dimmer:PIXI.Graphics;
    private _bigWinBackground:PIXI.spine.Spine;
    private _bigWinLvlContainer:PIXI.Container;
    private _bigWinParticleContainer:PIXI.Container;
    private _bigWinEmitter:Emitter;
    private _coinsFountain:BigWinCoinsFountain;
    private _coinsFountainContainer:PIXI.Container;
    private _bigWinText:PIXI.spine.Spine;
    private _layer:ScalableLayer;

    constructor() {
        super();
        this._coinsFountainContainer = new PIXI.Container();
        this._coinsFountain = new BigWinCoinsFountain(this._coinsFountainContainer);
        this._layer = StageManager.getLayer(BonusBunniesGameConfig.instance.LAYERS.BIG_WIN.name);
    }

    protected initAnimations():void {
        this._layer.addChild(this);

        this._dimmer = new PIXI.Graphics();
        this._dimmer.alpha = 0;
        this.addChild(this._dimmer);

        this._bigWinBackground = new PIXI.spine.Spine(GameResources.getSpineAsset("bigWinBackground"));
        this._bigWinBackground.visible = false;
        this._bigWinBackground.position.set(720 / 2, 720 / 2);
        this.addChild(this._bigWinBackground);

        this._bigWinParticleContainer = new PIXI.Container();
        this.addChild(this._bigWinParticleContainer);
        this._bigWinParticleContainer.position.set(360, 360);
        this._bigWinEmitter = this.createEmitters(this._bigWinParticleContainer, GameResources.getTextures("BigWin/particle_A"), ParticleConfigs.bigWinCenter);

        this.addChild(this._coinsFountainContainer);

        this._bigWinLvlContainer = new PIXI.Container();
        this._bigWinLvlContainer.alpha = 0;
        this.addChild(this._bigWinLvlContainer);

        this._bigWinText = new PIXI.spine.Spine(GameResources.getSpineAsset("BigWinText"));
        this._bigWinText.position.set(360, 260);
        this._bigWinLvlContainer.addChild(this._bigWinText);
    }

    private createEmitters(container:PIXI.Container, textures:PIXI.Texture[], config:any):Emitter {
        const emitter:Emitter = new Emitter(container, [{
            framerate : 30,
            loop : true,
            hasLoaded : true,
            textures : textures
        }], config);
        emitter.particleConstructor = AnimatedParticle;
        BonusBunniesParticleManager.addEmitter(emitter, false);
        return emitter;
    }

    protected onResize(resizeData:IResizeData):void {
        const screenWidth:number = resizeData.width;
        const screenHeight:number = resizeData.height;
        const leftOffset:number = 0 - ((resizeData.width - 720) / 2);
        const topOffset:number = 0 - ((resizeData.height - 720) / 2);

        this._dimmer.clear();
        this._dimmer.beginFill(0x000000, 0.8);
        this._dimmer.drawRect(leftOffset, topOffset, screenWidth, screenHeight);
    }

    public getBigWinLevelAnimation(level:number, winRatioConfig:IWinRatioConfig):Timeline {
        this.addChildAt(this._coinsFountainContainer, 3);
        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            () => {
                this.updateBigWinLevelAnimation(winRatioConfig.type!);
            },
            () => this._bigWinBackground.visible = true,
            () => this._bigWinBackground.state.setAnimation(0, "base", true),
            () => this._bigWinEmitter.emit = true,
            TweenLite.to(this._dimmer, 0.1, {alpha : 1}),
            TweenLite.to(this._bigWinLvlContainer, 0.2, {alpha : 1}),
            TweenLite.to(this._bigWinLvlContainer.scale, winRatioConfig.duration, {x : 1}),
            () => this._coinsFountain.startFountain(level)
        ]);

        return timeline;
    }

    private updateBigWinLevelAnimation(winType:WinRatioType, playSound:boolean = true):void {
        switch(winType) {
            case WinRatioType.SUPER_MEGA_WIN :
                this._bigWinText.skeleton.setSkin(null);
                this._bigWinText.skeleton.setSkinByName("LV3");
                this._bigWinText.skeleton.setSlotsToSetupPose();
                this._bigWinText.state.setAnimation(0, "FadeIn", false);
                if(playSound == true) {
                    SlotGame.sound.playEffect(BonusBunniesSoundConfig.instance.BIG_WIN_LEVEL);
                }
                return;
            case WinRatioType.MEGA_WIN :
                this._bigWinText.skeleton.setSkin(null);
                this._bigWinText.skeleton.setSkinByName("LV2");
                this._bigWinText.skeleton.setSlotsToSetupPose();
                this._bigWinText.state.setAnimation(0, "FadeIn", false);
                if(playSound == true) {
                    SlotGame.sound.playEffect(BonusBunniesSoundConfig.instance.BIG_WIN_LEVEL);
                }
                return;
            case WinRatioType.BIG_WIN :
                this._bigWinLvlContainer.alpha = 1;
                this._bigWinText.skeleton.setSkin(null);
                this._bigWinText.skeleton.setSkinByName("LV1");
                this._bigWinText.skeleton.setSlotsToSetupPose();
                this._bigWinText.state.setAnimation(0, "FadeIn", false);
                return;
            default :
        }
    }

    public playEndingAnimation(endWinRatioConfig:IWinRatioConfig):void {
        const timeline:TimelineLite = new TimelineLite();
        timeline.add(() => this._coinsFountain.stopFountain());
        timeline.add([
            () => this.updateBigWinLevelAnimation(endWinRatioConfig.type!, false),
            TweenLite.to(this._dimmer, 0.1, {alpha : 0, delay : 0.8}),
            TweenLite.fromTo(this._bigWinLvlContainer, 0.2, {alpha : 1}, {alpha : 0, delay : 0.8}),
            () => this._bigWinBackground.visible = false,
            () => this._bigWinEmitter.emit = false
        ]);
        this._bigWinBackground.state.clearTrack(0);

        timeline.play();
    }

    get coinsFountain():BigWinCoinsFountain {
        return this._coinsFountain;
    }

    get coinsFountainContainer():PIXI.Container {
        return this._coinsFountainContainer;
    }
}